'use client';

import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { X, CheckCircle2, AlertCircle, Info } from 'lucide-react';

interface ModalOptions {
  title: string;
  message: string;
  type?: 'success' | 'error' | 'info';
  confirmText?: string;
  onConfirm?: (inputValue?: string) => void;
  messageColor?: string;
  topRightSlot?: ReactNode;
  showInput?: boolean;
  inputPlaceholder?: string;
}

interface ModalContextType {
  openModal: (options: ModalOptions) => void;
  closeModal: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function MainModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [modalData, setModalData] = useState<ModalOptions | null>(null);
  const [inputValue, setInputValue] = useState('');

  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPendingClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const openModal = (options: ModalOptions) => {
    clearPendingClose();
    setInputValue('');
    setModalData(options);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    clearPendingClose();
    closeTimeoutRef.current = setTimeout(() => {
      setModalData(null);
      closeTimeoutRef.current = null;
    }, 200);
  };

  useEffect(() => {
    return () => clearPendingClose();
  }, []);

  useEffect(() => {
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('lenis-lock', { detail: { isLocked: true } }));
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      window.dispatchEvent(new CustomEvent('lenis-lock', { detail: { isLocked: false } }));
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleConfirm = () => {
    const currentInput = inputValue;
    closeModal();
    if (modalData?.onConfirm) modalData.onConfirm(currentInput);
  };

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}

      {isOpen && modalData && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6">
          {/* Только простое затемнение фона (без backdrop-blur) */}
          <div 
            className="absolute inset-0 bg-black/60 transition-opacity" 
            onClick={closeModal} 
          />
          
          {/* Строгий контейнер в стиле Material Design */}
          <div 
            className="relative w-full max-w-[420px] bg-[#1e1e1e] rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
            role="dialog"
            aria-modal="true"
          >
            
            {/* Кнопка закрытия и кастомный слот */}
            <div className="absolute top-3 right-3 flex items-center gap-2">
              {modalData.topRightSlot}
              <button 
                onClick={closeModal} 
                className="p-2 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 transition-colors outline-none cursor-pointer"
                aria-label="Закрыть"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 pt-7 text-left">
              <div className="flex items-start gap-4">
                
                {/* Иконка слева (Google Style) */}
                <div className="shrink-0 mt-0.5">
                  {modalData.type === 'success' && <CheckCircle2 size={24} className="text-green-500" />}
                  {modalData.type === 'error' && <AlertCircle size={24} className="text-red-500" />}
                  {(!modalData.type || modalData.type === 'info') && <Info size={24} className="text-blue-500" />}
                </div>
                
                <div className="flex-1 pr-6">
                  <h3 className="text-lg font-medium text-white mb-2">
                    {modalData.title}
                  </h3>
                  
                  <p className={`text-sm leading-relaxed whitespace-pre-line ${modalData.messageColor || 'text-zinc-400'}`}>
                    {modalData.message}
                  </p>

                  {/* Поле ввода */}
                  {modalData.showInput && (
                    <div className="mt-5">
                      <input 
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
                        placeholder={modalData.inputPlaceholder || 'Введите значение...'}
                        className="w-full bg-[#121212] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-blue-500 transition-colors"
                        autoFocus
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Действия (выровнены вправо) */}
              <div className="mt-8 flex justify-end">
                <button 
                  onClick={handleConfirm}
                  className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-colors outline-none cursor-pointer ${
                    modalData.type === 'error' 
                      ? 'bg-red-500 hover:bg-red-600 text-white' 
                      : 'bg-white hover:bg-zinc-200 text-black'
                  }`}
                >
                  {modalData.confirmText || 'Понятно'}
                </button>
              </div>
            </div>
            
          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
}

export const useMainModal = () => {
  const context = useContext(ModalContext);
  if (!context) throw new Error('useMainModal must be used within MainModalProvider');
  return context;
};